import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import ProductCard from "../components/ProductCard";
import { Search } from "lucide-react";

const FEATURED_CATEGORY_IDS = ["jewelry", "merch", "ai-prompts", "ebooks", "beauty", "home-goods"];

function isProductAvailable(p) {
  const variants = p.variants || [];
  if (!variants.length) return true;
  return variants.some((v) => v.stock === null || v.stock === undefined || (typeof v.stock === "number" && v.stock > 0));
}

function productMinPrice(p) {
  const variants = p.variants || [];
  const prices = variants.map((v) => v.price).filter((x) => typeof x === "number");
  if (prices.length) return Math.min(...prices);
  return Number(p.price) || 0;
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "all";
  const urlQuery = params.get("q") || "";
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState(urlQuery);
  const [loading, setLoading] = useState(true);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState("featured");
  const [priceRange, setPriceRange] = useState([0, 100000]);

  // Load ALL products once (so price range + filters work without re-fetching)
  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => {
      setProducts(r.data);
      const prices = r.data.map(productMinPrice).filter((x) => x > 0);
      if (prices.length) {
        const lo = Math.floor(Math.min(...prices));
        const hi = Math.ceil(Math.max(...prices));
        setPriceRange([lo, hi]);
      }
      setLoading(false);
    });
  }, []);

  // Sync URL ?q= changes (e.g. when user submits header search)
  useEffect(() => { setQuery(urlQuery); }, [urlQuery]);

  // Featured category cards (uses first product image per category as the visual)
  const categoryCards = useMemo(() => {
    return FEATURED_CATEGORY_IDS.map((cid) => {
      const meta = CATEGORIES.find((c) => c.id === cid);
      if (!meta) return null;
      const sample = products.find((p) => p.category === cid && p.image_url);
      return { ...meta, image: sample?.image_url || null };
    }).filter(Boolean);
  }, [products]);

  // Global price bounds from full catalog (for the slider's max attr)
  const priceBounds = useMemo(() => {
    const prices = products.map(productMinPrice).filter((x) => x > 0);
    if (!prices.length) return [0, 1000];
    return [Math.floor(Math.min(...prices)), Math.ceil(Math.max(...prices))];
  }, [products]);

  const filtered = useMemo(() => {
    let list = products;
    if (cat !== "all") list = list.filter((p) => p.category === cat);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
    }
    if (inStockOnly) list = list.filter(isProductAvailable);
    list = list.filter((p) => {
      const mp = productMinPrice(p);
      return mp >= priceRange[0] && mp <= priceRange[1];
    });
    switch (sortBy) {
      case "price-low": list = [...list].sort((a, b) => productMinPrice(a) - productMinPrice(b)); break;
      case "price-high": list = [...list].sort((a, b) => productMinPrice(b) - productMinPrice(a)); break;
      case "newest": list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      default: list = [...list].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
    return list;
  }, [products, cat, query, inStockOnly, priceRange, sortBy]);

  function setCat(id) {
    if (id === "all") setParams({});
    else setParams({ category: id });
  }

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
      <div className="mb-10">
        <div className="uppercase text-xs tracking-[0.25em] text-[#D4AF37] mb-3">The Shop</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Curated, calm, complete.</h1>
        <p className="mt-4 text-[#5C5547] max-w-2xl">
          Every prompt pack, ebook, garment and gadget — carefully chosen for the creators living a slow, considered life.
        </p>
      </div>

      {/* Featured category cards with imagery (Tiffany/Jared style) */}
      <div className="mb-10" data-testid="featured-categories">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 sm:gap-4">
          {categoryCards.map((c) => {
            const active = cat === c.id;
            return (
              <button key={c.id} onClick={() => setCat(c.id)}
                data-testid={`category-card-${c.id}`}
                className={`group flex flex-col items-center gap-2 transition ${active ? "opacity-100" : "hover:opacity-100 opacity-90"}`}>
                <div className={`w-full aspect-square rounded-full overflow-hidden bg-[#F5F2EA] border-2 transition ${active ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/30" : "border-[#EAE4D3] group-hover:border-[var(--accent)]/60"}`}>
                  {c.image ? (
                    <img src={c.image} alt={c.label} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-serif text-2xl text-[var(--accent)]/70">
                      {c.label.charAt(0)}
                    </div>
                  )}
                </div>
                <div className={`text-[11px] uppercase tracking-[0.15em] text-center ${active ? "text-[#2C2924] font-semibold" : "text-[#5C5547]"}`}>{c.short}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pill filters + secondary controls */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="category-filters">
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)}
            data-testid={`filter-${c.id}`}
            className={`px-4 py-1.5 rounded-full text-xs transition border ${
              cat === c.id ? "bg-[#2C2924] text-[#FDFBF7] border-[#2C2924]" : "bg-transparent text-[#5C5547] border-[#EAE4D3] hover:border-[#D4AF37] hover:text-[#2C2924]"
            }`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-8 p-4 rounded-2xl bg-[#FDFBF7] border border-[#EAE4D3]">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5547]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search prompts, ebooks, merch…"
            data-testid="shop-search-input"
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
        </div>

        <div className="flex flex-col gap-1 min-w-[14rem]">
          <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-[#5C5547]">
            <span>Price</span>
            <span data-testid="price-range-label">${priceRange[0]} – ${priceRange[1]}</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="range" min={priceBounds[0]} max={priceBounds[1]} value={priceRange[0]}
              data-testid="price-min"
              onChange={(e) => {
                const v = Math.min(parseInt(e.target.value, 10) || 0, priceRange[1]);
                setPriceRange([v, priceRange[1]]);
              }}
              className="flex-1 accent-[var(--accent)]" />
            <input type="range" min={priceBounds[0]} max={priceBounds[1]} value={priceRange[1]}
              data-testid="price-max"
              onChange={(e) => {
                const v = Math.max(parseInt(e.target.value, 10) || 0, priceRange[0]);
                setPriceRange([priceRange[0], v]);
              }}
              className="flex-1 accent-[var(--accent)]" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-[#2C2924] cursor-pointer" data-testid="in-stock-only">
          <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} className="accent-[var(--accent)]" />
          In stock only
        </label>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-by"
          className="px-4 py-2.5 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] text-xs focus:outline-none">
          <option value="featured">Featured</option>
          <option value="price-low">Price: low → high</option>
          <option value="price-high">Price: high → low</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {/* Results count */}
      <div className="mb-4 text-xs text-[#5C5547]" data-testid="results-count">
        {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "piece" : "pieces"}`}
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#5C5547]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" data-testid="shop-empty">
          <div className="font-serif text-2xl">Nothing matches those filters.</div>
          <div className="text-sm text-[#5C5547] mt-2">Try widening the price range or unchecking "In stock only".</div>
          <button onClick={() => { setQuery(""); setInStockOnly(false); setPriceRange(priceBounds); setCat("all"); }}
            data-testid="clear-filters"
            className="mt-6 px-6 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-xs">Clear filters</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
          {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
